import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Heading, Image } from "@zillow/constellation-10";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import React from "react";
const animationsDefaultTimingDuration = "0.2s";
const DEFAULT_EASING = "ease-out";
const progressBarTrackStyles = {
  width: "{spacing.150}",
  minWidth: "{spacing.150}",
  maxWidth: "{spacing.150}",
  flexShrink: 0,
  alignSelf: "stretch",
  backgroundColor: "gray.100",
  borderRadius: "{radii.capsule}",
  position: "relative",
  overflow: "hidden",
  boxSizing: "border-box"
};
const ProgressBar = ({
  active,
  animationTime,
  skipAnimation
}) => {
  const seconds = animationTime != null && animationTime > 0 ? animationTime : 6;
  return /* @__PURE__ */ jsx(Box, { css: progressBarTrackStyles, children: active && /* @__PURE__ */ jsx(motion.div, { initial: {
    height: "0%"
  }, animate: {
    height: "100%"
  }, transition: {
    duration: skipAnimation ? 0 : seconds,
    ease: "linear"
  }, style: {
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box"
  }, children: /* @__PURE__ */ jsx(Box, { css: {
    backgroundColor: "blue.600",
    width: "100%",
    height: "100%",
    minHeight: "100%",
    borderRadius: "{radii.capsule}",
    boxSizing: "border-box"
  } }) }) });
};
ProgressBar.propTypes = {
  active: PropTypes.bool,
  animationTime: PropTypes.number,
  skipAnimation: PropTypes.bool
};
const ListItemWithProgress = ({
  active,
  collapseOthers,
  animationTime,
  id,
  children,
  heading,
  onClick,
  skipAnimation
}) => {
  const divRef = React.useRef(null);
  return /* @__PURE__ */ jsxs(Box, { onClick, css: {
    cursor: "pointer",
    display: "flex",
    gap: "{spacing.600}",
    outline: "none",
    scrollMarginTop: "{spacing.600}",
    _focusVisible: {
      boxShadow: "shadows.focus"
    }
  }, "aria-expanded": !collapseOthers || active, "aria-label": heading, role: "button", tabIndex: 0, ref: divRef, id, onKeyDown: (e) => {
    if (["Enter", " "].includes(e.key)) {
      e.preventDefault();
      onClick(divRef.current);
    }
  }, children: [
    /* @__PURE__ */ jsx(ProgressBar, { animationTime, active, skipAnimation }),
    /* @__PURE__ */ jsxs(Box, { css: {
      display: "flex",
      flexDirection: "column",
      minWidth: "0"
    }, children: [
      /* @__PURE__ */ jsx(Heading, { as: "span", level: 5, css: {
        alignSelf: "flex-start",
        width: "max-content",
        maxWidth: "100%",
        color: active ? "blue.600" : void 0
      }, children: heading }),
      /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", initial: false, children: (!collapseOthers || active) && /* @__PURE__ */ jsx(motion.div, { initial: {
        height: 0,
        opacity: 0
      }, animate: {
        height: "auto",
        opacity: 1
      }, exit: {
        height: 0,
        opacity: 0
      }, style: {
        overflow: "hidden"
      }, transition: {
        duration: 0.2,
        ease: "easeOut"
      }, children }, "content") })
    ] })
  ] });
};
ListItemWithProgress.propTypes = {
  active: PropTypes.bool,
  heading: PropTypes.string,
  paragraph: PropTypes.string,
  animationTime: PropTypes.number,
  onClick: PropTypes.func,
  skipAnimation: PropTypes.bool,
  collapseOthers: PropTypes.bool
};
const imageCss = {
  objectFit: "contain",
  objectPosition: "center top",
  animationTimingFunction: "cubic-bezier(0.65, 0, 0.35, 1)",
  transition: `opacity ${animationsDefaultTimingDuration} ${DEFAULT_EASING}`,
  display: "block",
  /** v8-style intrinsic sizing inside capped wrapper (not full-column stretch). */
  width: "auto",
  height: "auto",
  maxWidth: "100%",
  maxHeight: "100%",
  minWidth: "0"
};
const fadeInAnimation = {
  "@keyframes mediaStepperFadein": {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },
  animation: `mediaStepperFadein ${animationsDefaultTimingDuration} ${DEFAULT_EASING}`
};
const fadeoutAnimation = {
  opacity: 0
};
const ImageWithProgress = ({
  active,
  media,
  reverse = false,
  mediaSizing = "v8",
  imgCss = {}
}) => {
  const fullColumn = mediaSizing === "fullColumn";
  const styles = {
    ...imageCss,
    ...fullColumn ? {
      width: "100%",
      height: "auto"
    } : {},
    ...active ? fadeInAnimation : fadeoutAnimation,
    ...imgCss
  };
  return /* @__PURE__ */ jsx(Box, { css: {
    width: "100%",
    maxWidth: fullColumn ? "100%" : "min(100%, 540px)",
    display: "flex",
    justifyContent: reverse ? "flex-start" : "flex-end",
    alignItems: "flex-start",
    alignSelf: "flex-start",
    height: "fit-content",
    minHeight: "0"
  }, children: /* @__PURE__ */ jsx(Image, { alt: imageAlt(media), src: imageSrc(media), width: media.width, height: media.height, css: styles, "aria-hidden": !active }) });
};
ImageWithProgress.propTypes = {
  active: PropTypes.bool,
  media: PropTypes.object,
  reverse: PropTypes.bool,
  mediaSizing: PropTypes.oneOf(["v8", "fullColumn"]),
  imgCss: PropTypes.object
};
({
  active: PropTypes.bool,
  media: PropTypes.object
});
function imageSrc(media, src) {
  return media ? media.src : src;
}
function imageAlt(media, alt) {
  return media && media.alt ? media.alt : alt;
}
export {
  ImageWithProgress,
  ListItemWithProgress,
  imageAlt,
  imageSrc
};
